import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Row, Col, Typography, Button, Divider, Spin, Table, App } from "antd";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useReactToPrint } from "react-to-print";
import ReportDom from "../../../../components/common/ReportDom";
import { manufacturingRecordWithDetailseApi } from "../../../../apiservice/manufacturing";
import { convertNumberToWords } from "../../../../lib/functions";
import ManufacturingVoucherBody from "../../ManufacturingVoucher/components/ManufacturingVoucherBody";
import { useShareDocument } from "../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../components/ShareButton/ShareDocumentButton";

const { Title } = Typography;

const ManufacturingQuickVoucher = () => {
  const { id } = useParams(); // /manufacturing/:id
  const navigate = useNavigate();
  const authInfo = useSelector((s) => s.auth.auth_info);
  const { message } = App.useApp();

  const [voucher, setVoucher] = useState(null);
  const [voucherNo, setVoucherNo] = useState("");
  const [accName, setAccName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const {
    shareOpen,
    setShareOpen,
    email,
    setEmail,
    handleEmailShare,
    handleWhatsAppShare,
  } = useShareDocument();

  const reportRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Manufacturing_Voucher_${voucher?.voucher_no || "Voucher"}`,
  });

  useEffect(() => {
    if (id) fetchVoucher();
  }, [id]);

  useEffect(() => {
    if (shareOpen && customerEmail) {
      setEmail(customerEmail);
    }
  }, [shareOpen, customerEmail, setEmail]);

  const fetchVoucher = async () => {
    try {
      setLoading(true);
      const res = await manufacturingRecordWithDetailseApi(id);
      if (res?.data?.length > 0) {
        setVoucher(res.data[0]);
      } else {
        message.warning("No Manufacturing Voucher data found.");
      }
    } catch (err) {
      console.error("Manufacturing voucher load failed:", err);
      message.error("Failed to load voucher.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!voucher) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Voucher Found</Title>
        <Button onClick={() => navigate("/manufacturing")}>Back</Button>
      </div>
    );
  }

  const rawDetails = voucher.mciDetails || voucher.rawDetails || [];
  const productionDetails = voucher.miDetails || voucher.productionDetails || [];
  const totalCost = Number(voucher.total_cost) || 0;

  // ---------------- RAW TABLE ----------------
  const rawColumns = [
  {
    title: "Raw Material",
    dataIndex: "item_name",
    render: (t, r) => (
      <>
        <div>{t}</div>
        <div style={{ fontSize: 12, color: "#555" }}>
          {r.raw_item_qty} {r.unit_symbol}
        </div>
      </>
    ),
  },
  {
    title: "Rate",
    dataIndex: "raw_item_rate",
    align: "right",
    render: (t) => Number(t || 0).toFixed(2),
  },
  {
    title: "Amount",
    align: "right",
    render: (r) => Number(r.raw_item_total || r.raw_item_qty * r.raw_item_rate || 0).toFixed(2),
  },
];

  // ---------------- PRODUCTION TABLE ----------------
  const prodColumns = [
  {
    title: "Finished Product",
    dataIndex: "item_name",
    render: (t, r) => (
      <>
        <div>{t}</div>
        <div style={{ fontSize: 12, color: "#555" }}>
          {r.item_qty} {r.unit_symbol}
        </div>
      </>
    ),
  },
  {
    title: "Rate",
    dataIndex: "item_rate",
    align: "right",
    render: (t) => Number(t || 0).toFixed(2),
  },
  {
    title: "Amount",
    align: "right",
    render: (r) => Number(r.item_total || r.item_qty * r.item_rate || 0).toFixed(2),
  },
];

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header Buttons */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/manufacturing")}
          >
            Back
          </Button>
        </Col>
        <Col>
          <div style={{ display: "flex", gap: 8 }}>
            <ShareDocumentButton
              disabled={!voucher}
              shareOpen={shareOpen}
              setShareOpen={setShareOpen}
              email={email}
              setEmail={setEmail}
              loading={loading}
              onEmail={() =>
                handleEmailShare({
                  url: `/print/manufacturing/${id}`,
                  fileName: `Manufacturing_${
                    voucherNo || voucher.voucher_no || "Voucher"
                  }.pdf`,
                  name: accName || "",
                  docNo:
                    voucherNo ||
                    voucher.voucher_no ||
                    "Voucher",
                })
              }
              onWhatsApp={() =>
                handleWhatsAppShare({
                  url: `/print/manufacturing/${id}`,
                  fileName: `Manufacturing_${
                    voucherNo || voucher.voucher_no || "Voucher"
                  }.pdf`,
                  text: `Hello ${accName || ""}, Your Manufacturing Voucher ${
                    voucherNo ||
                    voucher.voucher_no ||
                    "Voucher"
                  } is ready. Download here:`,
                })
              }
            />

            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print Voucher
            </Button>
          </div>
        </Col>
      </Row>

      {/* Voucher Display */}
      <div
        style={{
          background: "white",
          padding: 24,
          borderRadius: 8,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        {/* <Title level={4} style={{ textAlign: "center" }}>
          MANUFACTURING VOUCHER
        </Title> */}
        <Divider style={{ margin: "8px 0" }} />

        <Row gutter={24}>
          <Col span={12}>
            <p>
              <b>Voucher No:</b> {voucher.voucher_no}
            </p>
            <p>
              <b>Date:</b> {dayjs(voucher.created_date).format("DD MMM YYYY")}
            </p>
            <p>
              <b>Prepared By:</b> {voucher.full_name}
            </p>
          </Col>
        </Row>

        <Divider />

        {/* Raw Materials */}
        <Title level={5}>Raw Materials</Title>
        <Table
          columns={rawColumns}
          dataSource={rawDetails}
          pagination={false}
          bordered
          size="small"
          rowKey={(r) => r.mci_d_id || r.item_id}
        />

        <Divider />

        {/* Production */}
        <Title level={5}>Production</Title>
        <Table
          columns={prodColumns}
          dataSource={productionDetails}
          pagination={false}
          bordered
          size="small"
          rowKey={(r) => r.mf_d_id || r.item_id}
        />

        <Divider />

        <table style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td>
                <b>Labour Cost :</b>
              </td>
              <td align="right">{Number(voucher.labour_cost).toFixed(2)}</td>
            </tr>
            <tr>
              <td>
                <b>Other Cost :</b>
              </td>
              <td align="right">{Number(voucher.others_cost || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td colSpan={2}>
                <hr />
              </td>
            </tr>
            <tr>
              <td>
                <b>Total Cost :</b> {authInfo?.currency || "AED"}
              </td>
              <td align="right">{totalCost.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <Divider />

        <p>
          <b>In Words:</b>{" "}
          {convertNumberToWords(totalCost, authInfo?.currency || "AED")}
        </p>
        <p>
          <b>Description:</b> {voucher.description || ""}
        </p>
      </div>

      {/* Hidden Printable Report */}
      {/* <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="MANUFACTURING VOUCHER"
          filters={{
            fromDate: voucher.created_date,
            toDate: voucher.created_date,
          }}
          renderTable={() => (
            <>
              <Title level={5}>Raw Materials</Title>
              <Table
                columns={rawColumns}
                dataSource={rawDetails}
                pagination={false}
                bordered
                size="small"
                rowKey={(r) => r.mci_d_id || r.item_id}
              />
              <Divider />
              <Title level={5}>Production</Title>
              <Table
                columns={prodColumns}
                dataSource={productionDetails}
                pagination={false}
                bordered
                size="small"
                rowKey={(r) => r.mf_d_id || r.item_id}
              />
            </>
          )}
          extraSections={[
            {
              title: "Summary",
              columns: [
                { title: "Labour", dataIndex: "labour" },
                { title: "Other", dataIndex: "other" },
                { title: "Total", dataIndex: "total" },
              ],
              data: [
                {
                  labour: Number(voucher.labour_cost).toFixed(2),
                  other: Number(voucher.other_cost).toFixed(2),
                  total: totalCost.toFixed(2),
                },
              ],
            },
            {
              title: "Description",
              columns: [
                { title: "In Words", dataIndex: "inWords" },
                { title: "Narration", dataIndex: "narration" },
              ],
              data: [
                {
                  inWords: convertNumberToWords(
                    totalCost,
                    authInfo?.currency || "AED"
                  ),
                  narration: voucher.description || "",
                },
              ],
            },
          ]}
        />
      </div> */}
      <div style={{ display: "none" }}>
        <ManufacturingVoucherBody
          mf_id={id}
          setVoucherNo={setVoucherNo}
          setAccName={setAccName}
          setCustomerEmail={setCustomerEmail}
        />
      </div>
      <div
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
        }}
      >
        <ReportDom
          ref={reportRef}
          renderTable={() => (
            <ManufacturingVoucherBody
              mf_id={id}
              printMode
            />
          )}
        />
      </div>
      
    </div>
  );
};

export default ManufacturingQuickVoucher;
